import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { InspectionPhotos } from "@/components/InspectionPhotos";
import { SignatureSlot } from "@/components/SignaturePad";
import { CarDamageDiagram } from "@/components/CarDamageDiagram";
import { DocumentReader } from "@/components/DocumentReader";
import { DEFAULT_CLAUSES, DEFAULT_COSTS, APPROVAL_TEXT, CLAUSES_VERSION } from "@/lib/contractClauses";
import { ArrowLeft, Printer, Save, Loader2, BookmarkPlus, RotateCcw } from "lucide-react";

const fmtDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const money = (n) => (n || n === 0 ? new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2 }).format(n) : "");


const DEFAULT_COMPANY = {
  company_name: "Centro Auto Pontedera di Bartoli Massimo & C. SNC",
  company_address: "Via Don Primo Mazzolari 34/38 – 56025 Pontedera (PI)",
  company_vat: "", company_phone: "+39 328 355 9800", company_email: "info@centroautopontedera.it",
  km_included: "Illimitati", extra_km: "", fuel_policy: "Stesso livello del ritiro",
  ...Object.fromEntries(DEFAULT_COSTS.map(([k, , v]) => [k, v])),
  clauses: DEFAULT_CLAUSES, clauses_version: CLAUSES_VERSION,
};
const FUEL_LEVELS = ["Riserva", "1/4", "1/2", "3/4", "Pieno"];
// Older saved data carries the previous short clauses: drop them so the new Italian standard text applies
const upgrade = (d) => {
  if (!d) return {};
  if (d.clauses_version === CLAUSES_VERSION) return d;
  const { clauses, franchise, ...rest } = d;
  return { ...rest, clauses_version: CLAUSES_VERSION };
};
const TEMPLATE_KEYS = Object.keys(DEFAULT_COMPANY);

function buildDefaults(booking, car, template) {
  const c = booking.customer || {};
  return {
    ...DEFAULT_COMPANY, ...upgrade(template),
    contract_no: booking.code, contract_date: new Date().toLocaleDateString("it-IT"), place: "Pontedera",
    first_name: c.first_name || "", last_name: c.last_name || "", birth_date: "", birth_place: "", tax_code: "",
    address: "", city: "", phone: c.phone || "", email: c.email || "",
    license_no: "", license_by: "", license_issue: "", license_expiry: "", id_doc: "",
    driver2_name: "", driver2_license: "",
    vehicle: booking.car_name || `${car.brand || ""} ${car.model || ""}`.trim(), plate: car.plate || "",
    fuel: car.fuel || "", transmission: car.transmission || "", color: "", vin: "", reg_year: "",
    pickup_date: fmtDate(booking.pickup_date), pickup_time: "", pickup_location: booking.pickup_location || "",
    return_date: fmtDate(booking.return_date), return_time: "", return_location: booking.return_location || "",
    days: String(booking.days ?? ""), daily_rate: money(booking.daily_rate), total: money(booking.total_price),
    deposit: money(booking.deposit),
    payment: `${booking.payment_method === "pay_online" ? "Online (carta)" : "Al ritiro"} – ${booking.payment_status === "paid" ? "Pagato" : "Da pagare"}`,
    km_out: "", fuel_out: "", km_in: "", fuel_in: "", damages_out: "", damages_in: "", notes: "",
    marks_out: [], marks_in: [],
  };
}

const Ctx = createContext(null);

const F = ({ k, label, w = "flex-1", testid, options }) => {
const { c, set } = useContext(Ctx);
return (
  <label className={`${w} min-w-0 flex flex-col`}>
    <span className="text-[8.5px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</span>
    {options && <datalist id={`dl-${k}`}>{options.map((o) => <option key={o} value={o} />)}</datalist>}
    <input value={c[k] ?? ""} onChange={(e) => set(k)(e.target.value)} data-testid={testid || `contract-${k}`} list={options ? `dl-${k}` : undefined}
      className="bg-transparent border-0 border-b border-neutral-400 outline-none focus:bg-amber-50 focus:border-[#E63946] text-[12px] text-black py-0.5 px-0.5 w-full" />
  </label>
);
};
const Row = ({ children }) => <div className="flex gap-3 mb-2">{children}</div>;
const Sec = ({ title, children }) => (
  <section className="mb-3 break-inside-avoid">
    <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] bg-neutral-900 text-white px-2 py-1 mb-2">{title}</h3>
    {children}
  </section>
);


const AutoTextarea = ({ value, onChange, testid, className = "" }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current) { ref.current.style.height = "auto"; ref.current.style.height = `${ref.current.scrollHeight}px`; }
  }, [value]);
  return <textarea ref={ref} rows={2} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className={`w-full resize-none overflow-hidden bg-transparent outline-none focus:bg-amber-50 border border-neutral-300 rounded px-2 py-1 text-[11px] leading-snug ${className}`} />;
};

export default function ContractPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState("");
  const [photos, setPhotos] = useState([]);
  const [sigs, setSigs] = useState({});

  useEffect(() => {
    api.get(`/admin/bookings/${id}/contract`).then(({ data }) => {
      setMeta(data);
      setPhotos(data.booking.inspection_photos || []);
      setSigs(data.booking.signatures || {});
      setC({ ...buildDefaults(data.booking, data.car, data.template), ...upgrade(data.contract) });
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [id]);

  const [params, setParams] = useSearchParams();
  const autoPrint = params.get("stampa") === "1";
  useEffect(() => {
    if (!c || !autoPrint) return;
    setParams({}, { replace: true });
    const imgs = [...document.querySelectorAll("#contract-sheet img")];
    Promise.all(imgs.map((i) => (i.complete ? null : new Promise((r) => { i.onload = r; i.onerror = r; }))))
      .then(() => setTimeout(() => window.print(), 300));
  }, [c, autoPrint, setParams]);
  if (!c) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E63946]" /></div>;

  const set = (k) => (v) => setC((p) => ({ ...p, [k]: v }));
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const persist = async () => {
    const { data } = await api.put(`/admin/bookings/${id}/contract`, { data: c });
    if (data.dates_updated) {
      const days = String(data.days), total = money(data.total_price);
      setMeta((m) => ({ ...m, booking: { ...m.booking, pickup_date: data.pickup_date, return_date: data.return_date, days: data.days, total_price: data.total_price } }));
      if (c.days !== days || c.total !== total) {
        const next = { ...c, days, total };
        setC(next);
        await api.put(`/admin/bookings/${id}/contract`, { data: next });
      }
      toast.success(`Date aggiornate e bloccate in calendario: ${c.pickup_date} → ${c.return_date} (${days} giorni, € ${total})`);
    }
    if (data.dates_warning) toast.warning(data.dates_warning);
    return data;
  };
  const save = () => run("save", persist, "Contratto salvato");
  const saveTemplate = () => {
    const tpl = Object.fromEntries(TEMPLATE_KEYS.map((k) => [k, c[k]]));
    return run("tpl", async () => { await api.put("/admin/contract-template", { data: tpl }); setMeta((m) => ({ ...m, template: tpl })); },
      "Dati azienda e clausole salvati come modello per i prossimi contratti");
  };
  const reset = () => { setC(buildDefaults(meta.booking, meta.car, meta.template)); toast("Campi ripristinati dalla prenotazione (ricordati di salvare)"); };
  const print = async () => {
    try { await persist(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return; }
    await new Promise((r) => setTimeout(r, 200));
    window.print();
  };

  return (
    <Ctx.Provider value={{ c, set }}>
    <div className="min-h-screen bg-[#0B0C10] contract-page">
      <header className="no-print sticky top-0 z-30 bg-[#12141A]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent" data-testid="contract-back-btn">
            <Link to="/admin"><ArrowLeft className="w-4 h-4 mr-1.5" />Pannello</Link>
          </Button>
          <div className="font-display font-bold uppercase text-sm mr-auto">Contratto {c.contract_no}</div>
          <Button variant="outline" size="sm" onClick={reset} className="rounded-full border-white/15 bg-transparent" data-testid="contract-reset-btn" title="Ricarica i dati dalla prenotazione">
            <RotateCcw className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Ripristina</span>
          </Button>
          <Button variant="outline" size="sm" onClick={saveTemplate} disabled={!!busy} className="rounded-full border-white/15 bg-transparent" data-testid="contract-save-template-btn" title="Usa dati azienda, condizioni e clausole per tutti i prossimi contratti">
            {busy === "tpl" ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4 sm:mr-1.5" />}<span className="hidden sm:inline">Salva come modello</span>
          </Button>
          <Button variant="outline" size="sm" onClick={save} disabled={!!busy} className="rounded-full border-white/15 bg-transparent" data-testid="contract-save-btn">
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 sm:mr-1.5" />}<span className="hidden sm:inline">Salva</span>
          </Button>
          <Button size="sm" onClick={print} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="contract-print-btn">
            <Printer className="w-4 h-4 mr-1.5" />Stampa
          </Button>
        </div>
        <p className="max-w-5xl mx-auto px-4 pb-2 text-[11px] text-muted-foreground">Tutti i campi sono modificabili: clicca su un campo per scrivere. "Stampa" salva e apre la stampa (puoi anche scegliere "Salva come PDF").</p>
      </header>

      <DocumentReader bookingId={id} hasCustomerDocs={!!meta?.customer_docs?.length} savedDocs={meta?.admin_documents || []}
        onFill={(fields) => setC((p) => ({ ...p, ...fields }))} />
      <div className="py-6 px-2 overflow-x-auto">
        <div id="contract-sheet" data-testid="contract-sheet" className="bg-white text-black mx-auto w-[210mm] min-h-[297mm] p-[12mm] shadow-2xl font-sans">
          <div className="flex items-start justify-between gap-4 border-b-2 border-neutral-900 pb-3 mb-3">
            <div className="flex-1 min-w-0 space-y-0.5">
              <img src="/logo.png" alt="Centro Auto Pontedera" className="h-12 w-auto max-w-[62mm] object-contain mb-1.5" data-testid="contract-logo" />
              <input value={c.company_name} onChange={(e) => set("company_name")(e.target.value)} data-testid="contract-company_name"
                className="w-full bg-transparent outline-none focus:bg-amber-50 font-bold text-[15px] uppercase" />
              <input value={c.company_address} onChange={(e) => set("company_address")(e.target.value)} data-testid="contract-company_address" className="w-full bg-transparent outline-none focus:bg-amber-50 text-[11px]" />
              <div className="flex gap-2 text-[11px]">
                <span className="shrink-0">P.IVA</span><input value={c.company_vat} onChange={(e) => set("company_vat")(e.target.value)} data-testid="contract-company_vat" placeholder="__________" className="flex-1 min-w-0 bg-transparent outline-none focus:bg-amber-50" />
                <span className="shrink-0">Tel</span><input value={c.company_phone} onChange={(e) => set("company_phone")(e.target.value)} data-testid="contract-company_phone" placeholder="__________" className="flex-1 min-w-0 bg-transparent outline-none focus:bg-amber-50" />
              </div>
              <input value={c.company_email} onChange={(e) => set("company_email")(e.target.value)} data-testid="contract-company_email" className="w-full bg-transparent outline-none focus:bg-amber-50 text-[11px]" />
            </div>
            <div className="text-right shrink-0 w-48">
              <div className="text-[17px] font-extrabold uppercase leading-tight">Contratto di<br />noleggio veicolo</div>
              <div className="text-[9px] uppercase text-neutral-500 mt-1">senza conducente</div>
              <div className="mt-2 space-y-1"><F k="contract_no" label="N. contratto" /><F k="contract_date" label="Data" /></div>
            </div>
          </div>

          <Sec title="Cliente / conducente principale">
            <Row><F k="first_name" label="Nome" /><F k="last_name" label="Cognome" /><F k="tax_code" label="Codice fiscale" /></Row>
            <Row><F k="birth_place" label="Luogo di nascita" /><F k="birth_date" label="Data di nascita" w="w-28" /><F k="address" label="Indirizzo di residenza" w="flex-[2]" /></Row>
            <Row><F k="city" label="Città / CAP" /><F k="phone" label="Telefono" /><F k="email" label="Email" w="flex-[1.5]" /></Row>
            <Row><F k="license_no" label="Patente n." /><F k="license_by" label="Rilasciata da" /><F k="license_issue" label="Rilascio" w="w-24" /><F k="license_expiry" label="Scadenza" w="w-24" /><F k="id_doc" label="Documento identità n." /></Row>
            <Row><F k="driver2_name" label="Conducente aggiuntivo (nome e cognome)" w="flex-[2]" /><F k="driver2_license" label="Patente n." /></Row>
          </Sec>

          <Sec title="Veicolo">
            <Row><F k="vehicle" label="Marca e modello" w="flex-[2]" /><F k="plate" label="Targa" /><F k="fuel" label="Alimentazione" /><F k="transmission" label="Cambio" /></Row>
            <Row><F k="vin" label="N. telaio (VIN)" w="flex-[2]" /><F k="reg_year" label="Immatricolazione" /><F k="color" label="Colore" /></Row>
          </Sec>

          <Sec title="Periodo e condizioni economiche">
            <Row><F k="pickup_date" label="Ritiro – data" w="w-28" /><F k="pickup_time" label="Ora" w="w-16" /><F k="pickup_location" label="Luogo di ritiro" /></Row>
            <Row><F k="return_date" label="Riconsegna – data" w="w-28" /><F k="return_time" label="Ora" w="w-16" /><F k="return_location" label="Luogo di riconsegna" /></Row>
            <Row><F k="days" label="Giorni" w="w-14" /><F k="daily_rate" label="Tariffa giornaliera €" /><F k="total" label="Totale noleggio €" /><F k="deposit" label="Cauzione €" /><F k="payment" label="Pagamento" w="flex-[1.6]" /></Row>
            <Row><F k="km_included" label="Km inclusi" /><F k="extra_km" label="Costo km extra €" /><F k="fuel_policy" label="Carburante" w="flex-[1.5]" /></Row>
            <p className="text-[8.5px] text-neutral-500 -mt-1 no-print">Al salvataggio, le date di ritiro e riconsegna aggiornano la prenotazione e bloccano l'auto in calendario.</p>
          </Sec>

          <Sec title="Tabella costi extra e franchigie (€, IVA inclusa – addebitati solo se l'evento si verifica)">
            <div className="grid grid-cols-3 gap-x-3 gap-y-1.5" data-testid="contract-costs">
              {DEFAULT_COSTS.map(([k, label]) => <F key={k} k={k} label={label} />)}
            </div>
          </Sec>

          <Sec title="Stato del veicolo">
            <table className="w-full text-[11px] border-collapse mb-1">
              <thead><tr className="text-[8.5px] uppercase text-neutral-500"><th className="text-left w-24"></th><th className="text-left">Km</th><th className="text-left">Carburante</th><th className="text-left w-1/2">Danni / note</th></tr></thead>
              <tbody>
                {[["out", "Alla consegna"], ["in", "Al rientro"]].map(([s, l]) => (
                  <tr key={s} className="align-top">
                    <td className="font-semibold pt-2 pr-2">{l}</td>
                    <td className="pr-2"><F k={`km_${s}`} label="" /></td>
                    <td className="pr-2"><F k={`fuel_${s}`} label="" options={FUEL_LEVELS} /></td>
                    <td className="pt-1"><AutoTextarea value={c[`damages_${s}`]} onChange={set(`damages_${s}`)} testid={`contract-damages_${s}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-4 mt-2">
              <CarDamageDiagram title="Schema danni – consegna" marks={c.marks_out} onChange={set("marks_out")} testid="damage-diagram-out" />
              <CarDamageDiagram title="Schema danni – rientro" marks={c.marks_in} onChange={set("marks_in")} testid="damage-diagram-in" />
            </div>
            <p className="text-[8.5px] text-neutral-500 mt-1">I numeri sullo schema corrispondono alle note danni (es. "1 graffio paraurti"). Foto del veicolo conservate dal Locatore.</p>
          </Sec>

          <Sec title="Condizioni generali di noleggio">
            <AutoTextarea value={c.clauses} onChange={set("clauses")} testid="contract-clauses" className="text-[9.5px] border-dashed" />
          </Sec>

          <Sec title="Note">
            <AutoTextarea value={c.notes} onChange={set("notes")} testid="contract-notes" />
          </Sec>

          <div className="break-inside-avoid mt-4">
            <p className="text-[9.5px] text-neutral-700 mb-4" data-testid="contract-approval-text">{APPROVAL_TEXT}</p>
            <div className="flex gap-3 mb-8"><F k="place" label="Luogo" w="w-40" /><F k="contract_date" label="Data" w="w-32" testid="contract-sign-date" /></div>
            <div className="grid grid-cols-2 gap-12 text-[10px] text-center">
              <div><div className="h-16" /><div className="border-t border-neutral-800 pt-1">Firma del Locatore</div></div>
              <SignatureSlot bookingId={id} kind="main" label="Firma del Cliente" signature={sigs.main} onChange={(v) => setSigs((p) => ({ ...p, main: v }))} />
            </div>
            <div className="grid grid-cols-2 gap-12 text-[10px] text-center mt-4">
              <div />
              <SignatureSlot bookingId={id} kind="clauses" label="Firma del Cliente per approvazione specifica (artt. 1341-1342 c.c.)" signature={sigs.clauses} onChange={(v) => setSigs((p) => ({ ...p, clauses: v }))} />
            </div>
          </div>
        </div>
      </div>
      <InspectionPhotos bookingId={id} photos={photos} setPhotos={setPhotos} photosSentAt={meta?.booking?.photos_sent_at} />
    </div>
    </Ctx.Provider>
  );
}
